import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { Sparkline } from "@/components/Sparkline";

const STAGES = [
  { id: "reconnaissance", label: "Recon" },
  { id: "initial_access", label: "Access" },
  { id: "execution", label: "Exec" },
  { id: "discovery", label: "Discover" },
  { id: "credential_access", label: "Cred" },
  { id: "lateral_movement", label: "Lateral" },
  { id: "exfiltration", label: "Exfil" },
];

const KIND_BADGE: Record<string, string> = {
  win: "bg-ok/15 text-ok-400 border-ok/30",
  finding: "bg-warn/15 text-warn-400 border-warn/30",
  info: "bg-chrome-400/10 text-chrome-300 border-chrome-400/20",
};

const THROUGHPUT = [4, 6, 5, 9, 7, 11, 8, 14, 10, 12, 9, 16, 13, 11, 15, 12, 18, 14];

export function LiveDashboardPage(): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ["scans", "live"],
    queryFn: api.liveScan,
    refetchInterval: 5000,
  });

  if (isLoading) return <main className="text-chrome-300">Loading…</main>;

  if (!data) {
    return (
      <main>
        <PageHeader
          eyebrow="active scan"
          title="Live testing"
          subtitle="Start a scan to see the kill-chain progress in real time."
        />
        <div className="panel p-10 text-center text-sm text-chrome-400">
          No scan in progress. Trigger one with{" "}
          <code className="code text-cyber-400">POST /api/v1/scans/start</code>.
        </div>
      </main>
    );
  }
  if (error) return <main className="text-crit">Failed to load.</main>;

  return (
    <main>
      <PageHeader
        eyebrow="active scan"
        title="Live testing"
        subtitle={`Scan ${data.scan_id.slice(0, 18)}… · started ${new Date(data.started_at).toLocaleString()}`}
        right={
          <div className="flex items-center gap-2 rounded border border-crit/40 bg-crit/10 px-3 py-1.5 text-crit">
            <span className="pulse-dot text-crit" />
            <span className="text-[10px] font-medium uppercase tracking-widest">
              transmitting
            </span>
          </div>
        }
      />

      <section className="panel mb-6 p-6">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="label-overline">stage progression</div>
            <div className="mt-1 text-lg text-chrome-50">
              {STAGES[data.stage_index - 1]?.label ?? data.stage}{" "}
              <span className="text-chrome-400">
                · {data.stage_index} of {data.stage_count}
              </span>
            </div>
          </div>
          <div className="code text-xs text-chrome-400">
            ETA {new Date(data.estimated_completion_at).toLocaleTimeString()}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-1.5">
          {STAGES.slice(0, data.stage_count).map((stage, i) => {
            const idx = i + 1;
            const isDone = idx < data.stage_index;
            const isActive = idx === data.stage_index;
            const isFuture = idx > data.stage_index;
            return (
              <div
                key={stage.id}
                className={[
                  "relative flex flex-1 flex-col items-stretch overflow-hidden rounded-sm border",
                  isDone && "border-ok/40 bg-ok/10",
                  isActive && "border-cyber-400/50 bg-cyber-400/10 shadow-glow-cyber",
                  isFuture && "border-ink-400 bg-ink-700",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <span
                    className={[
                      "text-[11px] font-medium uppercase tracking-wider",
                      isDone && "text-ok-400",
                      isActive && "text-cyber-400 text-glow-cyber",
                      isFuture && "text-chrome-500",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {stage.label}
                  </span>
                  <span
                    className={[
                      "code text-[10px]",
                      isDone && "text-ok-400",
                      isActive && "text-cyber-400",
                      isFuture && "text-chrome-500",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {String(idx).padStart(2, "0")}
                  </span>
                </div>
                {isActive && (
                  <div className="h-0.5 bg-cyber-400/20">
                    <div className="h-full progress-shimmer" />
                  </div>
                )}
                {isDone && <div className="h-0.5 bg-ok" />}
                {isFuture && <div className="h-0.5 bg-ink-400" />}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {data.active_modules.map((m) => (
            <span
              key={m}
              className="flex items-center gap-1.5 rounded border border-cyber-400/30 bg-cyber-400/5 px-2 py-1 text-[11px] text-cyber-400"
            >
              <span className="pulse-dot text-cyber-400" />
              {m}
            </span>
          ))}
        </div>
      </section>

      <section className="mb-6 grid grid-cols-3 gap-4">
        <StatCard
          label="wins · today"
          value={data.wins_today}
          accent="ok"
          spark={THROUGHPUT}
        />
        <StatCard
          label="events · stream"
          value={data.events.length}
          accent="cyber"
          spark={[3, 2, 5, 4, 7, 6, 9, 8, 11, 10, 13, 12]}
        />
        <StatCard
          label="modules · active"
          value={data.active_modules.length}
          accent="warn"
          spark={[1, 2, 1, 2, 2, 3, 2, 2, 3, 2, 2, 2]}
        />
      </section>

      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="text-sm font-medium uppercase tracking-widest text-chrome-300">
            telemetry · live event stream
          </h2>
          <span className="text-[11px] text-chrome-400">refresh every 5s</span>
        </div>
        <ol className="panel divide-y divide-ink-400 overflow-hidden">
          {data.events.map((e, i) => (
            <li
              key={i}
              className="grid grid-cols-[auto_auto_1fr_auto] items-center gap-3 px-4 py-3 text-sm"
            >
              <span className="code text-[11px] text-chrome-500">
                {String(i + 1).padStart(3, "0")}
              </span>
              <span
                className={[
                  "rounded border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
                  KIND_BADGE[e.kind],
                ].join(" ")}
              >
                {e.kind}
              </span>
              <span className="text-chrome-100">{e.headline}</span>
              <span className="code text-[11px] text-chrome-400">
                {new Date(e.ts).toLocaleTimeString([], { hour12: false })}
              </span>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}

function StatCard({
  label,
  value,
  accent,
  spark,
}: {
  label: string;
  value: number;
  accent: "ok" | "cyber" | "warn";
  spark: number[];
}): JSX.Element {
  const color =
    accent === "ok" ? "#84CC16" : accent === "warn" ? "#F59E0B" : "#22D3EE";
  const textGlow =
    accent === "ok"
      ? "text-glow-ok"
      : accent === "warn"
        ? "text-glow-warn"
        : "text-glow-cyber";
  const textColor =
    accent === "ok"
      ? "text-ok-400"
      : accent === "warn"
        ? "text-warn-400"
        : "text-cyber-400";
  return (
    <div className="panel relative overflow-hidden p-5">
      <div className="label-overline">{label}</div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div
          className={[
            "font-mono text-4xl font-semibold tabular-nums",
            textColor,
            textGlow,
          ].join(" ")}
        >
          {String(value).padStart(2, "0")}
        </div>
        <Sparkline data={spark} color={color} width={140} height={36} />
      </div>
    </div>
  );
}
